/**
 * Storage persistence diagnostics.
 *
 * The widget runs in a cross-origin iframe, so the browser — not us — decides what kind of
 * storage bucket we get. A write that resolves is therefore NOT proof of persistence:
 * partitioned or ephemeral third-party buckets, private/secret browsing modes, embedded
 * WebViews with DOM storage disabled, and best-effort quota eviction all accept writes and
 * then discard them between sessions. These fields are what distinguish those cases, and are
 * captured at the moment a storage anomaly is detected.
 */

// Declared as a type alias rather than an interface so it satisfies the
// `Record<string, unknown>` constraint on AnalyticsService.handleWarning / handleError —
// interfaces get no implicit index signature and are rejected there.
export type StorageDiagnostics = {
  /** The localforage driver that actually won (indexeddb / localstorage), if storage opened. */
  driver?: string;
  /** True when running inside an iframe, where third-party storage rules apply. */
  framed: boolean;
  cookieEnabled: boolean;
  secureContext: boolean;
  /** False on old WebViews with no navigator.storage at all. */
  storageApi: boolean;
  quotaBytes?: number;
  usageBytes?: number;
  /** True only if the bucket is exempt from eviction; we never request it, so expect false. */
  persisted?: boolean;
  /** Whether this frame has unpartitioned storage access; absent where the API is unsupported. */
  storageAccess?: boolean;
  opaqueOrigin: boolean;
  /** How long the probe took — a slow probe is itself a signal of a wedged storage subsystem. */
  probeMs: number;
};

// Minimal structural types for APIs that lib.dom declares as always-present but which are
// genuinely missing on older Android WebViews — the population we are diagnosing.
type StorageProbes = {
  estimate?: () => Promise<{ quota?: number; usage?: number }>;
  persisted?: () => Promise<boolean>;
};

type StorageAccessProbe = {
  hasStorageAccess?: () => Promise<boolean>;
};

// Storage introspection is part of the subsystem under suspicion: on some Android WebViews
// these probes hang rather than reject. Cap each one so collecting diagnostics can never
// stall the caller — partial data still identifies the population.
const PROBE_TIMEOUT_MS = 250;

export async function getStorageDiagnostics(driver?: string | null): Promise<StorageDiagnostics> {
  const startedAt = Date.now();
  const storage: StorageProbes | undefined = navigator.storage;
  const doc: StorageAccessProbe = document;

  const estimate = await settleWithin(() => storage?.estimate?.());
  const persisted = await settleWithin(() => storage?.persisted?.());
  const storageAccess = await settleWithin(() => doc.hasStorageAccess?.());

  return {
    ...(driver && { driver }),
    framed: window.parent !== window,
    cookieEnabled: navigator.cookieEnabled,
    secureContext: window.isSecureContext,
    storageApi: storage !== undefined,
    // A sandboxed iframe without allow-same-origin runs on an opaque origin, where every
    // storage API is unavailable regardless of the user's browser settings.
    opaqueOrigin: window.origin === 'null',
    ...(estimate?.quota !== undefined && { quotaBytes: estimate.quota }),
    ...(estimate?.usage !== undefined && { usageBytes: estimate.usage }),
    ...(persisted !== undefined && { persisted }),
    ...(storageAccess !== undefined && { storageAccess }),
    probeMs: Date.now() - startedAt,
  };
}

async function settleWithin<T>(probe: () => Promise<T> | undefined): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const pending = probe();
    if (!pending) {
      return undefined;
    }
    return await Promise.race([
      pending,
      new Promise<undefined>((resolve) => {
        timer = setTimeout(() => resolve(undefined), PROBE_TIMEOUT_MS);
      }),
    ]);
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}
